/**
 * Manufacturing Profile Service
 * Handles API calls to check if the current user has a manufacturing unit assigned.
 * Follows the same pattern as gaushala/profileService.ts
 */

import { getServiceUrl } from '@/config/serviceConfig';

const API_BASE_URL = getServiceUrl('manufacturing');

// ============================================================================
// CUSTOM ERROR CLASS
// ============================================================================

/**
 * Custom error class for manufacturing-specific errors
 */
export class ManufacturingError extends Error {
  constructor(
    message: string,
    public code: 'NO_UNIT_ASSIGNED' | 'API_ERROR' | 'UNKNOWN',
    public originalError?: Error
  ) {
    super(message);
    this.name = 'ManufacturingError';
  }
}

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Get the current user's manufacturing unit profile.
 * Calls GET /api/v1/dashboard/my-unit which derives the unit from the JWT token.
 *
 * @returns Promise that resolves if user has a unit assigned
 * @throws ManufacturingError with code NO_UNIT_ASSIGNED if no unit is assigned
 */
export const getManufacturingProfile = async (): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/dashboard/my-unit`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Manufacturing Profile API Error Response:', errorData);

      // Check if the error is due to no manufacturing unit being assigned.
      // Treat 403 Forbidden as NO_UNIT_ASSIGNED: Spring's @PreAuthorize on /dashboard/my-unit
      // returns 403 when the user's role (e.g. FIELD_WORKER, SALES_TEAM) doesn't include
      // MFG_OPERATOR/ADMIN — which for our purposes means "no manufacturing access".
      const errorMessage = errorData?.message || errorData?.error || '';
      if (
        response.status === 403 ||
        response.status === 404 ||
        errorMessage.toLowerCase().includes('not found') ||
        errorMessage.toLowerCase().includes('not assigned') ||
        errorMessage.toLowerCase().includes('no manufacturing unit') ||
        errorMessage.toLowerCase().includes('forbidden') ||
        errorMessage.toLowerCase().includes('access denied')
      ) {
        throw new ManufacturingError(
          'No manufacturing unit is assigned to your account. Please contact your administrator.',
          'NO_UNIT_ASSIGNED'
        );
      }

      throw new ManufacturingError(
        errorData?.message || `API Error: ${response.status} ${response.statusText}`,
        'API_ERROR'
      );
    }

    const result = await response.json();
    console.log('Manufacturing Profile API Response:', result);

    const profileData = result.data || result;

    // Check if unit details are missing
    if (!profileData || (!profileData.id && !profileData.name)) {
      throw new ManufacturingError(
        'No manufacturing unit is assigned to your account. Please contact your administrator.',
        'NO_UNIT_ASSIGNED'
      );
    }

    return profileData;
  } catch (error) {
    console.error('Error fetching manufacturing profile:', error);

    // Re-throw ManufacturingError as is
    if (error instanceof ManufacturingError) {
      throw error;
    }

    // Wrap other errors
    throw new ManufacturingError(
      error instanceof Error ? error.message : 'Failed to load manufacturing profile',
      'UNKNOWN',
      error instanceof Error ? error : undefined
    );
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getAuthToken = (): string => {
  return localStorage.getItem('saubhagya_jwt_token') || '';
};
